import axios from 'axios';
import APIS from '../apiUrl';
import { requireAuthHeaders } from '../auth/getAuthToken';
import { GuestShareFormData } from '../../components/share-report-modal/GuestShareForm';
import { AuthUserInfo } from '../../redux/auth/types';
import { JobLog } from '../../models/jobLog';

/**
 * Info to be logged for each audit event.
 *
 * Each event has someone/something that did something to someone/something else,
 * which is possibly received by a third someone/something.  To name each of these,
 * we'll use the notion of a primary, secondary, and tertiary resource.
 *
 * In "Ted shared the report for job xyz with Jamie", the user "Ted" is
 * the primary resource, "the report for job xyz" is the secondary resource,
 * and the guest "Jamie" is the tertiary resource.
 *
 * So for the fields below, the primary resource id would be the MongoDB id for
 * the user Ted, the secondary resource id would be the MongoDB id for the log
 * for job xyz, and the tertiary resource id would be the email address for
 * Jamie.
 *
 * The description will have an English sentence describing the event, e.g.
 * "Registered user Ted Lasso shared the job report for 'xyz' with Jamie Tartt
 * for 30 days."
 *
 * Each of the functions applies this same idea as appropriate.
 */
export interface AuditEvent {
  eventName: string;
  primaryResourceId: string;
  secondaryResourceId: string;
  tertiaryResourceId?: string;
  description: string;
}

export const sendEventUserSharedJobReport = async (
  logInfo: JobLog | undefined,
  user: AuthUserInfo | undefined,
  shareInfo: GuestShareFormData,
) => {
  const [logId, logDescription] = getLogIdAndDescription(logInfo);
  const [userId, userGivenName, userFamilyName] = getUserIdAndName(user);

  const description =
    `Registered user ${userGivenName} ${userFamilyName} shared the job report for '${logDescription}'` +
    ` with ${shareInfo.firstName} ${shareInfo.lastName} of ${shareInfo.externalOrgName} for ${shareInfo.daysToExpiration} days.`;

  const auditEvent: AuditEvent = {
    eventName: 'user-shared-job-report-via-email',
    primaryResourceId: userId,
    secondaryResourceId: logId,
    tertiaryResourceId: shareInfo.emailAddress,
    description,
  };

  await sendEvent(auditEvent);
};

export const sendEventUserUnsharedEmailShare = async (
  logInfo: JobLog | undefined,
  user: AuthUserInfo | undefined,
  guestEmailAddress: string,
) => {
  const [logId, logDescription] = getLogIdAndDescription(logInfo);
  const [userId, userGivenName, userFamilyName] = getUserIdAndName(user);

  const description = `Registered user ${userGivenName} ${userFamilyName} unshared the job report for '${logDescription}' with ${guestEmailAddress}.`;
  const auditEvent: AuditEvent = {
    eventName: 'user-unshared-job-report-email-share',
    primaryResourceId: userId,
    secondaryResourceId: logId,
    tertiaryResourceId: guestEmailAddress,
    description,
  };

  await sendEvent(auditEvent);
};

export const sendEventGuestViewedJobReport = async (logInfo: JobLog | undefined, user: AuthUserInfo | undefined) => {
  const [logId, logDescription] = getLogIdAndDescription(logInfo);
  const [userId, guestGivenName, guestFamilyName] = getUserIdAndName(user);

  const description = `Guest ${guestGivenName} ${guestFamilyName} viewed the job report for '${logDescription}'.`;
  const auditEvent: AuditEvent = {
    eventName: 'guest-viewed-job-report',
    primaryResourceId: userId,
    secondaryResourceId: logId,
    description,
  };

  await sendEvent(auditEvent);
};

export const sendEventGuestTriedExpiredShare = async (logId: string, user: AuthUserInfo | undefined) => {
  const [userId, guestGivenName, guestFamilyName] = getUserIdAndName(user);

  const description = `Guest ${guestGivenName} ${guestFamilyName} tried expired share for log '${logId}'.`;
  const auditEvent: AuditEvent = {
    eventName: 'guest-tried-expired-job-report-share-link',
    primaryResourceId: userId,
    secondaryResourceId: logId,
    description,
  };

  await sendEvent(auditEvent);
};

const UNKNOWN = '???';

const getLogIdAndDescription = (logInfo: JobLog | undefined) => {
  return [logInfo?._id ?? UNKNOWN, logInfo?.description ?? UNKNOWN];
};

const getUserIdAndName = (user: AuthUserInfo | undefined) => {
  return [user?.id ?? UNKNOWN, user?.givenName ?? UNKNOWN, user?.familyName ?? UNKNOWN];
};

const sendEvent = async (auditEvent: AuditEvent) => {
  try {
    await axios({
      method: 'POST',
      url: `${APIS.SENTINEL_API}/api/audit-events/create`,
      data: auditEvent,
      headers: await requireAuthHeaders(),
    });
  } catch (error) {
    console.log(`failed to send ${auditEvent.eventName} audit event.`);
    // Don't rethrow because the user knows nothing about audit events.
  }
};
